import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from 'store';
import { createActionDispatcher } from 'store/create-action-dispatcher';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { fetcher } from 'utils/axios';

const initialState: Array<ChatUserModel> = [];

const unreadChatUsersSlice = createSlice({
  name: 'unreadChatUsers',
  initialState,
  reducers: {
    storeSetUnreadChatUsers: (state, action: PayloadAction<Array<ChatUserModel>>) => {
      return action.payload;
    },
    storeAddUnreadUser: (state, action: PayloadAction<ChatUserModel>) => {
      const user = action.payload;
      const existingUserIndex = state.findIndex(
        (u) => u.UserDisplayPhoneNumber === user.UserDisplayPhoneNumber,
      );
      if (existingUserIndex !== -1) {
        state[existingUserIndex] = { ...state[existingUserIndex], ...user };
      } else {
        state.push(user);
      }
    },
    storeProcessUnreadUser: (state, action: PayloadAction<ChatUserModel>) => {
      const updatedUser = action.payload;
      const existingUserIndex = state.findIndex((user) => user.Id === updatedUser.Id);

      const hasUnreadNow = Number(updatedUser.UnreadMessagesNumber) > 0;
      const hadUnreadBefore = existingUserIndex !== -1;

      //* Delete user from list if UnreadMessagesNumber set to 0
      if (!hasUnreadNow && hadUnreadBefore) {
        return state.filter((user) => user.UserDisplayPhoneNumber !== updatedUser.UserDisplayPhoneNumber);
      }
      //* Add user to list if UnreadMessagesNumber set from 0 to some number
      if (hasUnreadNow && !hadUnreadBefore) {
        return [updatedUser, ...state];
      }
      //* Just update user, if his UnreadMessagesNumber was not 0 and set to some number
      if (hasUnreadNow && hadUnreadBefore) {
        return state.map((user) =>
          user.UserDisplayPhoneNumber === updatedUser.UserDisplayPhoneNumber
            ? {
                ...user,
                ...updatedUser,
              }
            : user,
        );
      }

      //* default
      return state;
    },
    storeUpdateUnreadUser: (state, action: PayloadAction<{ id: number; params: Partial<ChatUserModel> }>) => {
      const { id, params } = action.payload;
      const existingUserIndex = state.findIndex((user) => user.Id === id);

      if (existingUserIndex !== -1) {
        state[existingUserIndex] = { ...state[existingUserIndex], ...params };
      }
    },
    storeUpdateUnreadUserByPhone: (
      state,
      action: PayloadAction<{ phone: string; params: Partial<ChatUserModel> }>,
    ) => {
      const { phone, params } = action.payload;
      const existingUserIndex = state.findIndex((user) => user.UserDisplayPhoneNumber === phone);

      if (existingUserIndex !== -1) {
        state[existingUserIndex] = { ...state[existingUserIndex], ...params };
      }
    },
    storeRemoveUnreadUser: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      const existingUserIndex = state.findIndex((u) => u.UserDisplayPhoneNumber === userId);
      if (existingUserIndex !== -1) {
        state.splice(existingUserIndex, 1);
      }
    },
    storeClearUnreadChatUsers: () => {
      return initialState;
    },
  },
});

export default unreadChatUsersSlice.reducer;
export const {
  storeSetUnreadChatUsers,
  storeAddUnreadUser,
  storeProcessUnreadUser,
  storeUpdateUnreadUser,
  storeUpdateUnreadUserByPhone,
  storeRemoveUnreadUser,
  storeClearUnreadChatUsers,
} = unreadChatUsersSlice.actions;

export const setUnreadChatUsers = createActionDispatcher(storeSetUnreadChatUsers);
export const getUnreadChatUsers = async () => {
  try {
    const data = await fetcher('get', '/api/services/getUnreadChatUsers', 'newapi');
    if (data) {
      dispatch(storeSetUnreadChatUsers(data.unreadContacts));
    }
  } catch (error) {
    console.warn('Error fetching chat users:', error);
  }
};
export const addUnreadUser = createActionDispatcher(storeAddUnreadUser);
export const processUnreadUser = createActionDispatcher(storeProcessUnreadUser);
export const updateUnreadUser = createActionDispatcher(storeUpdateUnreadUser);
export const updateUnreadUserByPhone = createActionDispatcher(storeUpdateUnreadUserByPhone);
export const removeUnreadUser = createActionDispatcher(storeRemoveUnreadUser);
export const clearUnreadChatUsers = createActionDispatcher(storeClearUnreadChatUsers);
